import { FC } from 'react';
import { Link } from '@mui/material';

import { PAYMENT_STEP } from '~common';
import { PassNoticeBanner } from '~components/atoms/pass/PassNoticeBanner';
import { LocalizationTypography } from '~components/organisms/localization';
import { usePaymentStepContext, useUserSubscriptionContext } from '~providers';
import { SubscriptionStatus } from '~types/subscription.type';

/**
 * Explains, on top of the pay-per-use steps, why the user's pass is not being
 * used for this rental. Renders nothing when there is no such situation.
 *
 * `RENEWAL_PENDING` in particular must never look like "you have no pass" —
 * the hold is alive, only the next period's charge is still retrying.
 */
export const PassStatusBanner: FC = () => {
  const { subscriptionStatus, activePass, isPassCanceled } =
    useUserSubscriptionContext();
  const { handleSetPaymentStep } = usePaymentStepContext();

  if (
    subscriptionStatus !== SubscriptionStatus.RENEWAL_PENDING &&
    subscriptionStatus !== SubscriptionStatus.NOT_USABLE_AT_STATION
  ) {
    return null;
  }

  const viewPassLink = activePass ? (
    <Link
      component="button"
      underline="always"
      onClick={() => handleSetPaymentStep(PAYMENT_STEP.PASS_DETAIL)}
      sx={{ color: 'inherit', alignSelf: 'flex-start' }}
    >
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.pass.viewPass"
        defaultText="View my pass"
        variant="Poppins/Subheading-sm"
        color="inherit"
      />
    </Link>
  ) : undefined;

  if (subscriptionStatus === SubscriptionStatus.RENEWAL_PENDING) {
    // The backend reports RENEWAL_PENDING for any held pass with no live
    // period, cancelled ones included — it keys off the hold existing, not off
    // a charge being retried. Cancelling stops the renewal, so promising a
    // retry here would describe something that is not going to happen.
    if (isPassCanceled) {
      return (
        <PassNoticeBanner tone="info" action={viewPassLink}>
          <LocalizationTypography
            textId="app.rentalPage.newStepThree.pass.canceledNoCoverage"
            defaultText="Your pass is cancelled, so it isn’t covering this rental."
            variant="Poppins/Body-sm"
            color="inherit"
          />
        </PassNoticeBanner>
      );
    }

    return (
      <PassNoticeBanner action={viewPassLink}>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.pass.renewalPending"
          defaultText="We couldn’t charge your card for the next billing cycle yet. You can still rent below while we retry."
          variant="Poppins/Body-sm"
          color="inherit"
        />
      </PassNoticeBanner>
    );
  }

  return (
    <PassNoticeBanner tone="info" action={viewPassLink}>
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.pass.notUsableAtStation"
        defaultText="Your pass isn’t valid at this station. You can still rent below."
        variant="Poppins/Body-sm"
        color="inherit"
      />
    </PassNoticeBanner>
  );
};
